import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { formStateHandler } from "../../../utils/utils";
import { transformPayload } from "../BranchGroupConstants";
import classes from "./index.module.css";
import ChipInputSelect from "../../../framework/ChipInputSelect";
import { fetchBranches } from "../../../services/branch-service";
import { saveBranchGroup } from "../../../services/branch-group-services";

const AddBranchGroup = ({
  id,
  onSaveBranch = () => {},
  onEscape = () => {},
  branchData = null,
}) => {
  const [formState, setFormState] = useState(null);
  const branchGroupNameRef = useRef("");
  const saveBranchGroupRef = useRef();
  const branchNameRef = useRef();
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [branches, setBranches] = useState([]);
  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback(async (fieldName, data) => {
    if (fieldName === "name" || !fieldName) {
      if (!data?.name?.trim()) {
        displayToast({
          type: "error",
          title: "Please enter a branch group name.",
        });
        return false;
      }
    }
    if (fieldName === "branches" || !fieldName) {
      if (!data?.branches?.length) {
        displayToast({
          type: "error",
          title: "Please select at least one branch.",
        });
        return false;
      }
    }
    return true;
  }, []);
  const updateBranchHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({
        ...formState,
      });
      return saveBranchGroup(payload).then((r) => {
        if (!r) return;
        onSaveBranch(r);
        return displayToast({
          type: "success",
          title: "Branch Group added successfully.",
        });
      });
    },
    [formState, onSaveBranch, validateForm]
  );
  const branchChangedHandler = (event, value, reason) => {
    fetchBranches().then((response) => {
      let result = Array.isArray(response?.result?.rows)
        ? response.result.rows
        : [];
      setBranches([...result, ...selectedBranches]);
    });
  };
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveBranchGroupRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (branchData) {
      const branches = Array.isArray(branchData.branchCode)
        ? branchData.branchCode
        : [];
      setFormState({
        ...branchData,
        branches: branches.map((branch) => branch),
      });
      setSelectedBranches(branches);
    }
  }, [branchData]);

  useEffect(() => {
    branchGroupNameRef.current?.focus();
  }, []);
  return (
    <div>
      <h4 className={classes["branch-grp-detail-label"]}>
        Branch Group Details
      </h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }} direction="column">
        <Grid item xs={6}>
          <TextInput
            id="branch_grp_name"
            ref={branchGroupNameRef}
            name="branchName"
            label="Branch Group"
            value={formState?.name}
            onFocus={() => setCurrentComponent("branch_grp_name")}
            onChange={(e) => {
              handleFormChange("name", e.target.value);
            }}
            placeholder="Enter Branch Group"
            onBlur={() => validateForm("name", formState)}
            maxInputLength={2000}
            required={true}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "branch_grp_name",
                down: "branch_name",
                left: "branch_grp_name",
                right: "branch_name",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ChipInputSelect
            label="Branches"
            id="branch_name"
            name="branches"
            ref={branchNameRef}
            onFocus={() => setCurrentComponent("branch_name")}
            value={selectedBranches}
            options={branches}
            onInputChange={branchChangedHandler}
            noOptionsText="Search for branches"
            getOptionLabel={(option) => option.code || option}
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            onBlur={() => validateForm("branches", formState)}
            onChange={(event, value) => {
              const branchCodeIds = value.map(
                (branch) => branch.code || branch
              );
              handleFormChange("branches", branchCodeIds);
              setSelectedBranches(value);
            }}
            disableCloseOnSelect={true}
            blurOnSelect={false}
            limitTags={5}
            markers={JSON.stringify({
              up: "branch_grp_name",
              down: "add_branch_save_button",
              left: "branch_grp_name",
              right: "add_branch_save_button",
            })}
          />
        </Grid>
      </Grid>
      <div className={classes["add-branch-grp-save-button-wrapper"]}>
        <Button
          id="add_branch_save_button"
          ref={saveBranchGroupRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Branch Group"
          color="violet"
          onClick={updateBranchHandler}
          markers={JSON.stringify({
            up: "branch_name",
            left: "branch_name",
            right: "add_branch_save_button",
            down: "add_branch_save_button",
          })}
        />
      </div>
    </div>
  );
};

export default AddBranchGroup;
